import React from 'react';
import { Truck, CreditCard, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface CheckoutSummaryProps {
  subtotal: number;
  /** Null while unknown. Zero is a real, free delivery — not a missing quote. */
  deliveryFee: number | null;
  tip?: number;
  discount?: number;
  total: number;
  /** Arrival window text, already formatted for display. */
  deliveryEstimate: string;
  /** True while a quote is in flight, so the row can show a spinner. */
  estimateLoading?: boolean;
  /** The shopper's default saved card, when they have one. */
  paymentMethod?: {
    brand: string;
    last4: string;
  };
  /** Opens payment method management. Omit to render the row as static. */
  onPaymentMethodClick?: () => void;
}

export function CheckoutSummaryCard({
  subtotal,
  deliveryFee,
  tip = 0,
  discount = 0,
  total,
  deliveryEstimate,
  estimateLoading = false,
  paymentMethod,
  onPaymentMethodClick
}: CheckoutSummaryProps) {
  const hasFee = deliveryFee !== null;

  return (
    <Card className="bg-purple-50/50 border-purple-100">
      <CardHeader>
        <CardTitle>Order Summary</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Cost Breakdown */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-body">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-body">
            <span>Delivery</span>
            {estimateLoading ? (
              <span className="flex items-center gap-1.5 text-primary">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="text-xs">Calculating</span>
              </span>
            ) : hasFee ? (
              <span>{deliveryFee === 0 ? 'Free' : `$${deliveryFee.toFixed(2)}`}</span>
            ) : (
              <span className="text-xs text-body italic">TBD</span>
            )}
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Promo Discount</span>
              <span>&minus;${discount.toFixed(2)}</span>
            </div>
          )}
          {tip > 0 && (
            <div className="flex justify-between text-body">
              <span>Driver Tip</span>
              <span>${tip.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-purple-100 my-2 pt-2 flex justify-between font-medium text-base text-heading">
            <span>Total</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Delivery Estimate */}
        <div className="bg-white rounded-xl p-4 flex items-start space-x-3 shadow-sm">
          <div className="p-2 bg-purple-100 rounded-lg text-primary">
            <Truck className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-heading text-sm">Estimated Delivery</p>
            {estimateLoading ? (
              <span className="flex items-center gap-1.5 text-sm text-primary">
                <Loader2 className="h-3 w-3 animate-spin" />
                Calculating your arrival window
              </span>
            ) : (
              <p className="text-sm text-body">{deliveryEstimate}</p>
            )}
          </div>
        </div>

        {/* Payment Method */}
        <button
          type="button"
          onClick={onPaymentMethodClick}
          disabled={!onPaymentMethodClick}
          className="w-full text-left bg-white rounded-xl p-4 flex items-center justify-between shadow-sm transition-colors enabled:hover:bg-gray-50 enabled:cursor-pointer"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg text-gray-600">
              <CreditCard className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-heading text-sm">Payment Method</p>
              <p className="text-sm text-body">
                {paymentMethod
                  ? `${paymentMethod.brand} ending in ${paymentMethod.last4}`
                  : 'Add a card for faster checkout'
                }
              </p>
            </div>
          </div>
          {onPaymentMethodClick && <ChevronRight className="h-4 w-4 text-gray-400" />}
        </button>
      </CardContent>
    </Card>
  );
}

